/**
 * Pure helpers for the shared Form wrapper (audit items #2, #9, #11, #62).
 * They take structural types so they can be tested without a DOM.
 */
import type {ErrorSummaryItem} from './ErrorSummary';

export type ParentLike={tagName:string;open?:boolean;parentElement:ParentLike|null};

/** Opens every collapsed <details> that contains the element; returns how many were opened. */
export function openCollapsedAncestors(element:{parentElement:ParentLike|null}):number{
  let opened=0;
  for(let parent=element.parentElement;parent;parent=parent.parentElement){
    if(parent.tagName.toUpperCase()==='DETAILS'&&parent.open===false){parent.open=true;opened+=1}
  }
  return opened;
}

export type ValidityLike={
  valueMissing?:boolean;typeMismatch?:boolean;tooShort?:boolean;tooLong?:boolean;
  rangeUnderflow?:boolean;rangeOverflow?:boolean;patternMismatch?:boolean;badInput?:boolean;stepMismatch?:boolean;
};
export type ControlMessageSource={validationMessage?:string;minLength?:number;maxLength?:number;min?:string;max?:string};

/** A specific, plain-language message instead of the browser's generic bubble text. */
export function messageForValidity(validity:ValidityLike,label:string,control:ControlMessageSource={}):string{
  const name=label||'This field';
  if(validity.valueMissing)return `${name} is required.`;
  if(validity.typeMismatch||validity.badInput||validity.patternMismatch)return `Enter a valid value for ${name.toLowerCase()}.`;
  if(validity.tooShort)return `${name} must be at least ${control.minLength??'the minimum number of'} characters.`;
  if(validity.tooLong)return `${name} must be at most ${control.maxLength??'the maximum number of'} characters.`;
  if(validity.rangeUnderflow)return `${name} must be ${control.min?`${control.min} or more`:'higher'}.`;
  if(validity.rangeOverflow)return `${name} must be ${control.max?`${control.max} or less`:'lower'}.`;
  return control.validationMessage||`${name} needs attention.`;
}

/** One summary line per field, in document order, without duplicates. */
export function dedupeIssues(items:readonly ErrorSummaryItem[]):ErrorSummaryItem[]{
  const seen=new Set<string>();
  return items.filter(item=>{if(seen.has(item.targetId))return false;seen.add(item.targetId);return true});
}

/** Guard messages are the same everywhere (audit item #2). */
export const UNSAVED_CHANGES={
  title:'Discard unsaved changes?',
  description:'You have changes that have not been saved. If you leave now they will be lost.',
  confirmLabel:'Discard',
  busyLabel:'Discarding…',
  cancelLabel:'Keep editing',
} as const;
